// ADR-0089 §3.4 / §4.7.3: subscribe to a hub agent-run's SSE event stream.
//
// EventSource handles reconnect automatically using Last-Event-ID, so this hook
// is intentionally thin — it just opens the stream, accumulates events, and
// surfaces a small machine-state for the calling component to drive its UI.
//
// On unmount or when `runId` flips, the prior stream is closed cleanly.

import { useEffect, useRef, useState } from "react";

export type AgentRunStreamEvent = {
  /** Monotonic ordinal assigned by the hub (used by EventSource as Last-Event-ID). */
  ordinal: number;
  /** Namespaced kind, e.g. "query_conversation.attempt_started". */
  kind: string;
  /** JSON-parsed payload from the SSE data line. */
  payload: Record<string, unknown>;
};

export type AgentRunStreamStatus =
  | "idle"
  | "connecting"
  | "streaming"
  | "ended"
  | "error";

export type AgentRunStreamHookResult = {
  events: AgentRunStreamEvent[];
  status: AgentRunStreamStatus;
  /** Bumps on every browser-initiated reconnect attempt. Useful for UI badges. */
  reconnectAttempt: number;
};

export function useAgentRunStream(runId: string | null): AgentRunStreamHookResult {
  const [events, setEvents] = useState<AgentRunStreamEvent[]>([]);
  const [status, setStatus] = useState<AgentRunStreamStatus>("idle");
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Track which runId the current events array belongs to. When runId flips,
  // we drop the prior events so the consumer never sees a mixed stream.
  const eventsRunRef = useRef<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setStatus("idle");
      return;
    }
    if (eventsRunRef.current !== runId) {
      setEvents([]);
      eventsRunRef.current = runId;
    }
    setStatus("connecting");

    const url = `/api/tenant/agent-runs/${encodeURIComponent(runId)}/events`;
    // `withCredentials: true` so the session cookie is sent — the SSE endpoint
    // is gated by requireTenantContext like every other /tenant/* route.
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = (): void => {
      setStatus("streaming");
    };

    es.onerror = (): void => {
      // The browser auto-reconnects; surface the attempt for UI badging.
      // EventSource doesn't expose specific error categories — onerror fires
      // for both transient network drops and stream-end. If readyState moves
      // to CLOSED, treat it as an unrecoverable error.
      setReconnectAttempt((n) => n + 1);
      if (es.readyState === EventSource.CLOSED) {
        setStatus("error");
      }
    };

    // Listen for the full set of conversation-mode event kinds (spec §4.7.5).
    // Each handler stamps the ordinal from the `id:` line so consumer logic
    // can order events even across reconnects.
    const conversationEventKinds = [
      "query_conversation.run_started",
      "query_conversation.attempt_started",
      "query_conversation.attempt_sql_emitted",
      "query_conversation.attempt_executing",
      // ADR-0119: live stage/rows while the SQL runs on the connector.
      "query_conversation.attempt_progress",
      "query_conversation.attempt_succeeded",
      "query_conversation.attempt_failed",
      "query_conversation.attempt_analyzing",
      "query_conversation.attempt_analyzed",
      "query_conversation.final",
      "query_conversation.ended",
      // ADR-0115 — Model Advisor run events.
      "model_advisor.started",
      "model_advisor.completed",
      "model_advisor.failed",
      "model_advisor.ended"
    ];

    function handle(ev: MessageEvent): void {
      let payload: Record<string, unknown> = {};
      try {
        const parsed = JSON.parse(ev.data) as unknown;
        if (parsed && typeof parsed === "object") {
          payload = parsed as Record<string, unknown>;
        }
      } catch {
        // Malformed payload — keep ordinal and kind, drop body.
      }
      const ordinal = Number(ev.lastEventId);
      setEvents((prev) => [
        ...prev,
        {
          ordinal: Number.isFinite(ordinal) ? ordinal : prev.length + 1,
          kind: ev.type,
          payload
        }
      ]);
      if (ev.type.endsWith(".ended")) {
        setStatus("ended");
        es.close();
      }
    }

    for (const kind of conversationEventKinds) {
      es.addEventListener(kind, handle);
    }

    return () => {
      for (const kind of conversationEventKinds) {
        es.removeEventListener(kind, handle);
      }
      es.close();
    };
  }, [runId]);

  return { events, status, reconnectAttempt };
}
